import {
  ArrowDownLeft,
  ArrowUpRight,
  Banknote,
  BookOpen,
  Calculator,
  Pin,
  PinOff,
  Wallet,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  listPinnedBalances,
  unpinAccount,
  type PinnedAccountBalance,
} from "../../../api/dashboard";
import { formatMinor } from "../../../lib/money";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyT = (...args: any[]) => unknown;

function safeT(
  t: AnyT,
  key: string,
  fallback?: string,
  opts?: Record<string, unknown>,
): string {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tAny = t as any;
    if (typeof tAny.exists === "function" && !tAny.exists(key)) throw new Error("missing key");
    const raw = opts ? t(key, opts) : t(key);
    if (typeof raw === "string" && raw.trim().length > 0 && raw !== key) return raw;
  } catch {
    /* ignore */
  }
  if (fallback && fallback.length > 0) return fallback;
  return "";
}

function displayPinnedName(t: AnyT, row: PinnedAccountBalance): string {
  if (typeof row.nameCustom === "string" && row.nameCustom.trim().length > 0) return row.nameCustom;
  const key = row.nameI18nKey;
  if (key && key.trim().length > 0) {
    const tr = safeT(t, key, "");
    if (tr.length > 0) return tr;
  }
  if (typeof row.displayName === "string" && row.displayName.trim().length > 0) return row.displayName;
  return `Account ${row.accountCode}`;
}

function pickAccent(row: PinnedAccountBalance): {
  accentClass: string;
  iconClass: string;
  Icon: typeof Wallet;
} {
  const code = row.accountCode || "";
  const tag = (row.colorTag || "").toLowerCase();
  if (tag.includes("emerald") || tag.includes("green")) {
    return {
      accentClass: "bg-emerald-500",
      iconClass: "bg-emerald-50 text-emerald-700",
      Icon: Wallet,
    };
  }
  if (tag.includes("sky") || tag.includes("blue")) {
    return {
      accentClass: "bg-sky-500",
      iconClass: "bg-sky-50 text-sky-700",
      Icon: BookOpen,
    };
  }
  if (tag.includes("amber") || tag.includes("yellow") || tag.includes("orange")) {
    return {
      accentClass: "bg-amber-500",
      iconClass: "bg-amber-50 text-amber-700",
      Icon: ArrowUpRight,
    };
  }
  if (tag.includes("indigo") || tag.includes("violet") || tag.includes("purple")) {
    return {
      accentClass: "bg-indigo-500",
      iconClass: "bg-indigo-50 text-indigo-700",
      Icon: ArrowDownLeft,
    };
  }
  const first = code.charAt(0);
  if (first === "1") {
    return {
      accentClass: "bg-emerald-500",
      iconClass: "bg-emerald-50 text-emerald-700",
      Icon: Wallet,
    };
  }
  if (first === "2" || first === "3") {
    return {
      accentClass: "bg-amber-500",
      iconClass: "bg-amber-50 text-amber-700",
      Icon: ArrowUpRight,
    };
  }
  if (first === "4") {
    return {
      accentClass: "bg-indigo-500",
      iconClass: "bg-indigo-50 text-indigo-700",
      Icon: ArrowDownLeft,
    };
  }
  if (first === "5") {
    return {
      accentClass: "bg-sky-500",
      iconClass: "bg-sky-50 text-sky-700",
      Icon: Calculator,
    };
  }
  return {
    accentClass: "bg-slate-500",
    iconClass: "bg-slate-50 text-slate-700",
    Icon: Banknote,
  };
}

interface Props {
  refreshKey?: number;
  canWrite?: boolean;
  /** Render only cards (no section chrome) for mini balance row */
  compact?: boolean;
}

export function PinnedAccountBalancesGrid({
  refreshKey = 0,
  canWrite = false,
  compact = false,
}: Props) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<PinnedAccountBalance[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listPinnedBalances();
      setRows(res.balances);
      setAsOf(res.asOf);
    } catch (err) {
      setRows([]);
      setAsOf(null);
      setError(err instanceof Error ? err.message : "pinned load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const handleUnpin = useCallback(
    async (pinId: string) => {
      setRemovingId(pinId);
      try {
        await unpinAccount(pinId);
      } catch {
        /* ignored; reload will reflect state */
      } finally {
        setRemovingId(null);
        void load();
      }
    },
    [load],
  );

  if (loading) {
    if (compact) return null;
    return (
      <section aria-label={safeT(t, "dashboard.pinned_section", "الحسابات المثبتة")}>
        <div className="mb-1 flex items-center gap-1.5">
          <Pin className="h-4 w-4 text-godo-navy" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-slate-900">
            {safeT(t, "dashboard.pinned_section", "الحسابات المثبتة")}
          </h2>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-lg border border-slate-200 bg-slate-100"
            />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    if (compact) return null;
    return (
      <section aria-label={safeT(t, "dashboard.pinned_section", "الحسابات المثبتة")}>
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      </section>
    );
  }

  if (rows.length === 0) {
    if (compact) return null;
    return (
      <section aria-label={safeT(t, "dashboard.pinned_section", "الحسابات المثبتة")}>
        <div className="mb-1 flex items-center gap-1.5">
          <Pin className="h-4 w-4 text-godo-navy" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-slate-900">
            {safeT(t, "dashboard.pinned_section", "الحسابات المثبتة")}
          </h2>
        </div>
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-xs text-slate-500">
          <PinOff className="mx-auto mb-1 h-6 w-6 text-slate-400" aria-hidden="true" />
          <p>{safeT(t, "dashboard.pinned_empty_hint", "لا توجد حسابات مثبتة بعد. اذهب إلى دليل الحسابات واضغط على 📌 لتثبيت الحسابات الأهم لك هنا.")}</p>
        </div>
      </section>
    );
  }

  const cards = rows.map((row) => {
    const signed = row.balanceMinor;
    const signedStr = formatMinor(signed);
    const naturalStr = row.creditNormal
      ? signed < 0
        ? `Dr ${formatMinor(Math.abs(signed))}`
        : signed > 0
          ? formatMinor(signed)
          : formatMinor(0)
      : signed > 0
        ? formatMinor(signed)
        : signed < 0
          ? `Cr ${formatMinor(Math.abs(signed))}`
          : formatMinor(0);
    const label = displayPinnedName(t, row);

    if (compact) {
      return (
        <article key={row.pinId} className="dash-balance-card group relative" title={label}>
          <p className="dash-balance-label">{label}</p>
          <p className="dash-balance-value" dir="ltr">
            {naturalStr}
          </p>
          {canWrite ? (
            <button
              type="button"
              onClick={() => void handleUnpin(row.pinId)}
              disabled={removingId === row.pinId}
              className="absolute left-1 top-1 rounded p-0.5 text-[9px] text-slate-400 opacity-0 group-hover:opacity-100 hover:text-red-600"
              aria-label={safeT(t, "dashboard.unpin_action", "إلغاء التثبيت")}
            >
              ×
            </button>
          ) : null}
        </article>
      );
    }

    const { accentClass, iconClass, Icon } = pickAccent(row);
    return (
      <article
        key={row.pinId}
        className="group relative overflow-hidden rounded-lg border border-slate-200 bg-white p-2 shadow-sm transition hover:shadow-md"
      >
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 h-1 ${accentClass}`}
          aria-hidden="true"
        />
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-slate-900" title={label}>
              {label}
            </p>
            <p className="mt-1 text-xl font-bold tracking-tight text-godo-navy" dir="ltr" title={signedStr}>
              {naturalStr}
            </p>
          </div>
          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconClass}`}>
            <Icon className="h-5 w-5" aria-hidden="true" />
          </div>
        </div>
      </article>
    );
  });

  if (compact) {
    return <>{cards}</>;
  }

  return (
    <section aria-label={safeT(t, "dashboard.pinned_section", "الحسابات المثبتة")}>
      <div className="mb-1 flex items-center gap-1.5">
        <Pin className="h-4 w-4 text-godo-navy" aria-hidden="true" />
        <h2 className="text-sm font-semibold text-slate-900">
          {safeT(t, "dashboard.pinned_section", "الحسابات المثبتة")}
        </h2>
        {asOf ? (
          <span className="text-[10px] text-slate-400">
            {safeT(t, "dashboard.as_of", "حتى تاريخ")} · {asOf}
          </span>
        ) : null}
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">{cards}</div>
    </section>
  );
}
